import { Injectable } from '@angular/core';
import {
  AlertController,
  LoadingController,
  ModalController,
  ToastController,
} from '@ionic/angular';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { CustomErrorAlertComponent } from '../components/custom-alert/custom-alert.component';
import { ProgressLoadingComponent } from '../modals/progress-loading/progress-loading.component';
import { TutorialComponent } from '../modals/tutorial/tutorial.component';
import { Contest } from '../models/contest';

@Injectable({
  providedIn: 'root',
})
export class ToolsService {

  role: any;
  start: boolean = false;

  private loadingState = new BehaviorSubject<{
    loading: boolean;
    phrase: string;
    gif?: string;
  }>({ loading: false, phrase: '' });
  private progressLoadingModal?: HTMLIonModalElement;
  public progressLoadingChangeText: Subject<string> = new Subject<string>();
  public progressLoadingChangeProgress: Subject<number> = new Subject<number>();
  gifDefault = 'assets/img/loading4.gif';
  loading: any;


  private goToLoginSource = new Subject<void>();
  goToLogin$ = this.goToLoginSource.asObservable();

  constructor(
    private toastController: ToastController,
    private modalCtrl: ModalController,
    private alertCtrl: AlertController,
    private loadingController: LoadingController
  ) {}

  async showLoading(message: string = 'Cargando...') {
    if (this.loading) return;
    this.loading = await this.loadingController.create({
      message: message,
    });
    await this.loading.present();
  }

  hideLoading() {
    if (!this.loading) return;
    this.loading.dismiss();
    this.loading = null;
  }

  get loadingState$() {
    return this.loadingState.asObservable();
  }

  startLoading(initialPhrase: string) {
    this.loadingState.next({
      loading: true,
      phrase: initialPhrase,
      gif: this.gifDefault,
    });
  }

  updateLoadingPhrase(newPhrase: string, gif?: string) {
    this.loadingState.next({
      loading: true,
      phrase: newPhrase,
      gif: gif ? gif : this.gifDefault,
    });
  }

  stopLoading() {
    this.loadingState.next({ loading: false, phrase: '' });
  }

  getValueFromQuestion(name: string, questions: Array<any>): any {
    const question = questions.find((question) => question.label === name);

    if (question) {
      const value = question.formControl.value;
      if (question.inputType === 'date') {
        return value?.toString().trim() ? value : null;
      }
      return value?.toString().trim() ? value : ' ';
    }
    return null;
  }

  getValueFromBillId(name: string, questions: Array<any>): any {
    let question = questions.find((question) => question.id === name);
    if (question) return question.formControl.value;
    return null;
  }

  getValueFromLoginQuestion(questionId: string, questions: Array<any>): any {
    let question = questions.find((question) => question.id === questionId);
    if (question) return question.formControl.value;
    return null;
  }

  async presentErrorToast(message: string) {
    const toast = await this.toastController.create({
      position: 'top',
      message: message,
      color: 'danger',
      duration: 8000,
    });
    toast.present();
  }

  async presentSucessToast(message: string) {
    const toast = await this.toastController.create({
      position: 'top',
      message: message,
      color: 'success',
      duration: 8000,
    });
    toast.present();
  }

  async presentProgressLoading(progress: number, text: string) {
    if (this.progressLoadingModal) return;
    console.debug(
      'Present progress loading',
      this.progressLoadingChangeText,
      this.progressLoadingChangeProgress
    );
    this.progressLoadingModal = await this.modalCtrl.create({
      component: ProgressLoadingComponent,
      cssClass: 'progress-loading-modal',
      backdropDismiss: false,
      componentProps: {
        progress: progress,
        text: text,
      },
    });
    await this.progressLoadingModal.present();
  }

  async updateProgressLoading(progress?: number, text?: string) {
    // Validate if the modal exists
    if (!this.progressLoadingModal) return;
    // Update the progress if the param was sent
    if (progress) this.progressLoadingChangeProgress.next(progress);
    // Update the text if the param was sent
    if (text) this.progressLoadingChangeText.next(text);
  }

  async dismissProgressLoading() {
    if (!this.progressLoadingModal) return;
    await this.progressLoadingModal?.dismiss();
    this.progressLoadingModal = undefined;
  }

  async convertObservableToPromise<T>(observable: Observable<T>): Promise<T> {
    return new Promise((resolve, reject) => {
      observable.subscribe({
        next: (data: any) => {
          return resolve(data);
        },
        error: (err: any) => {
          return reject(err);
        },
      });
    });
  }

  async showBillErrorAlert(errors: any) {
    let hasErrors = false;
    let notRecognizedFields = '';
    for (let key in errors) {
      if (errors[key]) {
        notRecognizedFields += `<li>${key}</li>`;
        hasErrors = true;
      }
    }
    if (!hasErrors) return;

    const modal = this.modalCtrl.create({
      component: CustomErrorAlertComponent,
      cssClass: 'custom-alert-modal',
      componentProps: {notRecognizedFields}
    });

    (await modal).present();
  }

  async showTutorialModal() {
    let showTutorial = await this.getShowTutorialAgain();
    if (!showTutorial) return;
    const modal = await this.modalCtrl.create({
      component: TutorialComponent,
      cssClass: 'tutorial-modal',
      backdropDismiss: true,
    });
    await modal.present();
  }

  async setShowTutorialAgain(showTutorial: boolean) {
    localStorage.setItem('showTutorialAgain', showTutorial ? 'true' : 'false');
  }

  async getShowTutorialAgain(): Promise<boolean> {
    return new Promise((resolve, reject) => {
      let showTutorial = localStorage.getItem('showTutorialAgain');
      console.debug('ToolsService.getShowTutorialAgain()', showTutorial);
      if (!showTutorial || showTutorial === 'true') return resolve(true);
      return resolve(false);
    });
  }

  triggerGoToLogin() {
    this.goToLoginSource.next();
  }
}
